import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { db, firebaseApp } from '@/lib/firebase';
import { espAtletasWA } from '@/lib/waLive';
import { esUrlEA, espAtletasEA } from '@/lib/eaLive';

/** Fotos para la TARJETA DE FINAL de WhatsApp (diseño v4, 2026-08-06).
 *
 *  Sube el RECORTE SIN FONDO (PNG con transparencia) de cada español que
 *  vaya a disputar una final. El motor automático y el botón "Final con
 *  foto" del modal buscan la foto aquí; si no está, publican el formato
 *  clásico — subirla ANTES de la final.
 *
 *  Almacenamiento (decisión 2026-08-06: NO tocar rules compartidas):
 *  - Storage: `fotos-atletas/final--<clave>.png` (regla existente: whitelist,
 *    image/*, <2MB; el prefijo `final--` separa estos recortes de las fotos
 *    de perfil, que son `fotosAtletas/map`).
 *  - Firestore: doc `fotosAtletas/finales`, `entries: { CLAVE: {url, path} }`.
 *  - CLAVE = nombre sin acentos, MAYÚSCULAS, espacios colapsados (idéntica a
 *    auto_wa._clave_foto). Relevos: `SPAIN|<CLAVE(evento)>`.
 */

// Campeonatos cubiertos: acceso de un clic (2026-08-07). La URL de EA lleva
// ?code=ECH26 forzado (el sondeo del slug resolvería Rieti).
const CAMPEONATOS: [string, string][] = [
  ['Mundial U20 · Oregón', 'https://worldathletics.org/competitions/world-athletics-u20-championships/oregon26'],
  ['Europeo · Birmingham', 'https://live.european-athletics.com/birmingham-2026?code=ECH26'],
];
const URL_DEFECTO = CAMPEONATOS[0][1];

/** Réplica EXACTA de auto_wa._clave_foto (Python): si cambias una, cambia la otra. */
function clave(nombre: string): string {
  return nombre
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toUpperCase()
    .split(/\s+/)
    .filter(Boolean)
    .join(' ');
}

function claveDe(a: { nombre: string; evento: string; esRelevo: boolean }): string {
  return a.esRelevo ? `SPAIN|${clave(a.evento)}` : clave(a.nombre);
}

interface Entrada {
  url?: string;
  path?: string;
}

export function FotosFinales() {
  const [url, setUrl] = useState(URL_DEFECTO);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [atletas, setAtletas] = useState<{ nombre: string; evento: string; esRelevo: boolean }[]>([]);
  const [entries, setEntries] = useState<Record<string, Entrada>>({});
  const [subiendo, setSubiendo] = useState<string | null>(null);
  const [filtro, setFiltro] = useState('');

  async function cargar(u: string) {
    setCargando(true);
    setError(null);
    try {
      const [lista, snap] = await Promise.all([
        esUrlEA(u) ? espAtletasEA(u) : espAtletasWA(u),
        getDoc(doc(db, 'fotosAtletas', 'finales')),
      ]);
      setAtletas(lista);
      setEntries((snap.data()?.entries as Record<string, Entrada>) ?? {});
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    void cargar(URL_DEFECTO);
  }, []);

  async function subir(a: { nombre: string; evento: string; esRelevo: boolean }, file: File) {
    const k = claveDe(a);
    setSubiendo(k);
    setError(null);
    try {
      if (file.type !== 'image/png') {
        throw new Error('Debe ser PNG con transparencia (recorte sin fondo).');
      }
      if (file.size >= 2 * 1024 * 1024) {
        throw new Error('Máximo 2 MB (regla de Storage). Reduce la imagen.');
      }
      // Nombre de fichero plano: la regla de Storage es de UN nivel
      // (fotos-atletas/{file}), nada de subcarpetas.
      const path = `fotos-atletas/final--${k.replace(/[^A-Z0-9]+/g, '-')}.png`;
      const r = ref(getStorage(firebaseApp), path);
      await uploadBytes(r, file, { contentType: 'image/png' });
      const u = await getDownloadURL(r);
      await setDoc(
        doc(db, 'fotosAtletas', 'finales'),
        { entries: { [k]: { url: u, path, nombre: a.nombre, evento: a.evento, updatedAt: Date.now() } } },
        { merge: true },
      );
      setEntries((prev) => ({ ...prev, [k]: { url: u, path } }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubiendo(null);
    }
  }

  const visibles = useMemo(() => {
    const f = filtro.trim().toLowerCase();
    if (!f) return atletas;
    return atletas.filter((a) => `${a.nombre} ${a.evento}`.toLowerCase().includes(f));
  }, [atletas, filtro]);

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 16px' }}>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>Fotos de finales (tarjeta WhatsApp)</h1>
      <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
        Recortes SIN FONDO (PNG, &lt;2 MB, mejor ≥1000 px de alto). Una tarjeta por español
        con foto en cada final; sin foto, sale el formato clásico. Los relevos llevan una
        foto por prueba (el equipo).
      </p>
      {/* Cambio de campeonato de un clic (Mundial ↔ Europeo). El listado va
          en ORDEN DE PARTICIPACIÓN: lo de arriba es lo que urge. */}
      <div className="ld-row" style={{ gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        {CAMPEONATOS.map(([titulo, u]) => (
          <button
            key={u}
            type="button"
            className={`ld-btn ld-btn--small ${url === u ? 'ld-btn--primary' : 'ld-btn--ghost'}`}
            disabled={cargando}
            onClick={() => { setUrl(u); void cargar(u); }}
          >
            {titulo}
          </button>
        ))}
      </div>
      <div className="ld-row" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          style={{ flex: 1, minWidth: 280 }}
          placeholder="URL del campeonato (worldathletics.org o live.european-athletics.com)"
        />
        <button type="button" className="ld-btn" disabled={cargando} onClick={() => void cargar(url)}>
          {cargando ? 'Cargando…' : 'Cargar atletas'}
        </button>
      </div>
      <input
        value={filtro}
        onChange={(e) => setFiltro(e.target.value)}
        placeholder="Filtrar por nombre o prueba…"
        style={{ width: '100%', marginBottom: 12 }}
      />
      {error ? <div className="ld-login__error" style={{ marginBottom: 12 }}>{error}</div> : null}
      {!cargando && atletas.length === 0 ? (
        <p className="muted">Sin atletas ESP (¿startlists aún no publicadas?).</p>
      ) : null}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {visibles.map((a) => {
          const k = claveDe(a);
          const e = entries[k];
          return (
            <div
              key={k}
              className="ld-row"
              style={{
                gap: 12, alignItems: 'center', padding: '10px 12px',
                border: '1px solid rgba(128,128,128,.35)', borderRadius: 10, flexWrap: 'wrap',
              }}
            >
              {e?.url ? (
                <img src={e.url} alt="" style={{ width: 44, height: 44, objectFit: 'contain' }} />
              ) : (
                <span style={{ width: 44, height: 44, display: 'inline-block', opacity: .25 }}>—</span>
              )}
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontWeight: 600 }}>
                  {a.esRelevo ? `Equipo España — ${a.evento}` : a.nombre}
                </div>
                <div className="muted mono" style={{ fontSize: 11 }}>
                  {a.esRelevo ? k : a.evento}
                  {e?.url ? ' · foto subida ✓' : ' · sin foto'}
                </div>
              </div>
              <label className="ld-btn" style={{ cursor: subiendo ? 'wait' : 'pointer' }}>
                {subiendo === k ? 'Subiendo…' : e?.url ? 'Reemplazar' : 'Subir foto'}
                <input
                  type="file"
                  accept="image/png"
                  style={{ display: 'none' }}
                  disabled={subiendo !== null}
                  onChange={(ev) => {
                    const f = ev.target.files?.[0];
                    ev.target.value = '';
                    if (f) void subir(a, f);
                  }}
                />
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default FotosFinales;
